import { Box, FormHelperText, InputLabel, SxProps, Theme } from "@mui/material";
import { DateTimePicker, DateTimeValidationError } from "@mui/x-date-pickers";
import { Field, FieldProps } from "formik";
import { DateTime } from "luxon";
import { ReactElement } from "react";

import theme from "~frontend/theme";

export default function FormikDateTimePicker({
  name,
  label,
  minDate,
  minDateError,
  maxDate,
  maxDateError,
  fullWidth,
  helperText,
  sx,
  disabled,
}: {
  name: string;
  label: string;
  minDate?: DateTime;
  minDateError?: string;
  maxDate?: DateTime;
  maxDateError?: string;
  fullWidth?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
  disabled?: boolean;
}): ReactElement {
  const errorString = (error: DateTimeValidationError) => {
    switch (error) {
      case "minDate":
        return minDateError;
      case "maxDate":
        return maxDateError;
      default:
        return "";
    }
  };

  return (
    <Field name={name}>
      {({ field, form, meta }: FieldProps<DateTime>) => {
        return (
          <Box sx={{ width: fullWidth ? "100%" : undefined, ...sx }}>
            {label ? <InputLabel shrink>{label}</InputLabel> : null}
            <DateTimePicker
              value={field.value}
              onChange={async newValue => {
                await form.setFieldValue(field.name, newValue);
              }}
              onError={async newError => {
                if (newError) {
                  form.setFieldError(field.name, errorString(newError));
                  await form.setFieldTouched(field.name, true);
                } else {
                  form.setFieldError(field.name, undefined);
                }
              }}
              minDateTime={minDate}
              maxDateTime={maxDate}
              sx={{
                paddingTop: label ? 0 : undefined,
                width: fullWidth ? "100%" : undefined,
                marginBottom: 0,
                "& .MuiInputAdornment-root": { width: "48px" },
              }}
              disabled={disabled}
              // disable the placeholder when the field is disabled
              slotProps={disabled ? { textField: { placeholder: "" } } : {}}
            />
            {(meta.touched && meta.error) || helperText ? (
              <FormHelperText
                sx={{
                  paddingLeft: theme.spacing(2),
                  paddingTop: 0,
                  marginTop: 0,
                  fontSize: "10px",
                }}
                error={meta.touched && !!meta.error}
              >
                {(meta.touched && meta.error) || helperText}
              </FormHelperText>
            ) : null}
          </Box>
        );
      }}
    </Field>
  );
}
